import type {
  ConceptFromSignalResponse,
  DecisionEnvelope,
  CostCoverResponse,
  CascadeRunOutput,
  CascadeRunRequest,
  DriverNode,
  Disposition,
  DriftPacketResponse,
  DriftPacketSummary,
  EngineSummary,
  EvoValue,
  EvolutionGraphConfig,
  EvolutionHistoryEvent,
  EvolutionLearningDatasetRow,
  GraphEdge,
  HardConstraint,
  ExclusivesResponse,
  FandomResponse,
  FormulationPipeline,
  FormulationRunResponse,
  HealthResponse,
  MnAModelResponse,
  MnATarget,
  PremortemResponse,
  PricingMixResponse,
  ProfitAtRiskResponse,
  SectorShockResponse,
  ShelfResponse,
  TradeSpendResponse,
  LearningResponse,
  MonteCarloResponse,
  NorthstarLiveResponse,
  ScoredOption,
  Signal,
  SignalInjectResponse,
  SpineResponse,
  RegistryResponse,
  ValidationEvidence,
  Twin,
  WargameRequest,
  WargameResponse,
} from '../types';

export interface DriverTreeResponse {
  config_version: string;
  apex_node_id: string;
  selected_node_id: string;
  orientation: 'left_to_right';
  nodes: DriverNode[];
  edges: GraphEdge[];
  hard_constraints: HardConstraint[];
  all_pending: boolean;
}

export interface EvolutionPrediction {
  prediction_id: string;
  prediction_type: import('../types').EvolutionPredictionType;
  engine_ref: import('../types').EngineRef;
  intervention_id: string;
  entity_id: string;
  state_predicted: string;
  current_state: Record<string, EvoValue>;
  projected_state: Record<string, EvoValue | number>;
  confidence: number;
  precursor_evidence: string[];
  model_ref: {
    model_id: string;
    trained_through: string;
    validation_status: string;
  };
  projected_tripwire: {
    breached: boolean;
    rule_id: string;
    threshold: EvoValue;
    projected_crossing_date: string | null;
  };
  recommended_action: import('../types').EvolutionRecommendedAction;
}

// V4.1 simulation disposition (handoffV3 §10) — logs sim-derived decisions
// to the institutional memory, surfaces in the Learning Loop.
export type SimulationKind = 'wargame' | 'profit_at_risk' | 'pricing_mix' | 'sector_shock' | 'pre_mortem';

export interface SimulationDispositionRequest {
  simulation_kind: SimulationKind;
  signal_id: string;
  disposition: Disposition;
  owner: string;
  recommendation: string;
  predicted_outcome?: Record<string, unknown>;
  // V8 §15.2a: a real decision control — approve / modify / override-with-reason.
  decision?: DecisionEnvelope;
}

export interface SimulationDispositionResponse {
  decision_log_id: string;
  status: 'logged';
}

// Demo passcode header — the backend's middleware checks this on /api/* (except /api/health).
// For dev, set VITE_DEMO_PASSCODE to match backend .env DEMO_PASSCODE (default: arizona-c2-demo).
const DEMO_PASSCODE = import.meta.env.VITE_DEMO_PASSCODE ?? 'arizona-c2-demo';
const PASSCODE_HEADER = 'x-demo-passcode';

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    headers: {
      'Content-Type': 'application/json',
      [PASSCODE_HEADER]: DEMO_PASSCODE,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const j = await res.json();
      detail = (j as { detail?: string }).detail ?? detail;
    } catch {
      // ignore
    }
    throw new Error(`${res.status} ${detail}`);
  }
  return res.json() as Promise<T>;
}

async function requestForm<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { [PASSCODE_HEADER]: DEMO_PASSCODE }, // no Content-Type: the browser sets the multipart boundary
    body: form,
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const j = await res.json();
      detail = (j as { detail?: string }).detail ?? detail;
    } catch {
      // ignore
    }
    throw new Error(`${res.status} ${detail}`);
  }
  return res.json() as Promise<T>;
}

export const api = {
  health: () => request<HealthResponse>('GET', '/api/health'),
  spine: () => request<SpineResponse>('GET', '/api/spine'),
  registry: () => request<RegistryResponse>('GET', '/api/registry'),
  signals: () => request<Signal[]>('GET', '/api/signals'),
  twins: () => request<Twin[]>('GET', '/api/twins'),
  scoring: () => request<ScoredOption[]>('GET', '/api/scoring'),
  scoringWithWeights: (weights: Record<string, number>) =>
    request<ScoredOption[]>('POST', '/api/scoring/weights', weights),
  injectSignal: (id: string, value: number) =>
    request<SignalInjectResponse>('POST', `/api/signals/${id}/inject`, { value }),
  driftList: () => request<DriftPacketSummary[]>('GET', '/api/drift'),
  drift: (id: string) => request<DriftPacketResponse>('GET', `/api/drift/${id}`),
  driftWaitCost: (id: string) =>
    request<{ summary: string }>('POST', `/api/drift/${id}/wait-cost`),
  setDisposition: (id: string, disposition: string, owner: string, decision?: DecisionEnvelope, predicted_outcome?: Record<string, unknown>) =>
    request<{ decision_log_id: string }>('POST', `/api/drift/${id}/disposition`, {
      disposition,
      owner,
      decision,
      predicted_outcome,
    }),
  wargame: (body: WargameRequest) => request<WargameResponse>('POST', '/api/wargame/run', body),
  montecarlo: (iterations: number, shocks: Record<string, number>) =>
    request<MonteCarloResponse>('POST', '/api/montecarlo/run', { iterations, shocks }),
  learning: () => request<LearningResponse>('GET', '/api/learning'),
  recordLearning: (decision_log_id: string, actual_outcome: Record<string, unknown>) =>
    request<{ updated_weights: unknown[]; calibration_note: string }>(
      'POST',
      '/api/learning/record',
      { decision_log_id, actual_outcome },
    ),

  // ───── V3 Engine Room ─────
  engines: () => request<EngineSummary[]>('GET', '/api/engines'),

  // Engine 1 — Formulation
  formulationRun: (brief: string, constraints: Record<string, unknown>) =>
    request<FormulationRunResponse>('POST', '/api/formulation/run', { brief, constraints }),
  formulationPipeline: () => request<FormulationPipeline>('GET', '/api/formulation/pipeline'),

  // Engine 2 — Supply
  supplyProfitAtRisk: (iterations: number, shocks: Record<string, number>) =>
    request<ProfitAtRiskResponse>('POST', '/api/supply/profit-at-risk', { iterations, shocks }),
  supplyCostCover: () => request<CostCoverResponse>('GET', '/api/supply/cost-cover'),

  // Engine 3 — Channel
  channelShelf: () => request<ShelfResponse>('GET', '/api/channel/shelf'),
  channelTradeSpend: (program: Record<string, unknown>) =>
    request<TradeSpendResponse>('POST', '/api/channel/trade-spend', { program }),
  channelExclusives: () => request<ExclusivesResponse>('GET', '/api/channel/exclusives'),

  // Engine 4 — Brand
  brandFandom: () => request<FandomResponse>('GET', '/api/brand/fandom'),
  brandConceptFromSignal: (social_signal: string) =>
    request<ConceptFromSignalResponse>('POST', '/api/brand/concept-from-signal', { social_signal }),

  // Engine 6 — M&A
  mnaTargets: () => request<MnATarget[]>('GET', '/api/mna/targets'),
  mnaModel: (target_id: string) =>
    request<MnAModelResponse>('POST', '/api/mna/model', { target_id }),

  // V4.1 — Simulation disposition (handoffV3 §10): logs a sim-derived decision
  // to decision_log; appears in /api/learning as source="simulation".
  simulationDisposition: (body: SimulationDispositionRequest) =>
    request<SimulationDispositionResponse>('POST', '/api/simulations/disposition', body),

  // V5.3 — Pricing & Mix simulation
  pricingMixRun: (price_changes_pct: Record<string, number>, handoff_to_wargame = true) =>
    request<PricingMixResponse>('POST', '/api/simulation/pricing-mix/run', { price_changes_pct, handoff_to_wargame }),

  // V5.4 — Sector Concentration Shock
  sectorShockRun: (removed_supplier_id: string, severity = 1.0) =>
    request<SectorShockResponse>('POST', '/api/simulation/sector-shock/run', { removed_supplier_id, severity }),

  // V5.5 — Pre-Mortem (backward causal chains + deterministic evidence scorer)
  preMortemRun: (failure_statement: string, horizon_quarters = 4, top_k = 3) =>
    request<PremortemResponse>('POST', '/api/simulation/pre-mortem/run', { failure_statement, horizon_quarters, top_k }),

  // ═════════ V6 — CPG value-chain engines ═════════
  // Generic GET for the data-driven hygiene sub-tabs (typed at call site).
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),

  // C2 Evolution preview endpoints
  evolutionNorthstarConfig: () =>
    request<EvolutionGraphConfig>('GET', '/api/evolution/northstar/config'),
  evolutionNorthstarLive: (scenario?: string) => {
    const qs = scenario ? `?scenario=${encodeURIComponent(scenario)}` : '';
    return request<NorthstarLiveResponse>('GET', `/api/evolution/northstar/live${qs}`);
  },
  evolutionDriverTree: (selected_node_id?: string) => {
    const qs = selected_node_id ? `?selected_node_id=${encodeURIComponent(selected_node_id)}` : '';
    return request<DriverTreeResponse>('GET', `/api/evolution/northstar/driver-tree${qs}`);
  },
  evolutionCascadeRun: (body: CascadeRunRequest) =>
    request<CascadeRunOutput>('POST', '/api/evolution/cascade/run', body),
  evolutionCascade: (cascade_id: string) =>
    request<CascadeRunOutput>('GET', `/api/evolution/cascade/${cascade_id}`),
  evolutionPredictions: () =>
    request<EvolutionPrediction[]>('GET', '/api/evolution/predictions'),
  evolutionPrediction: (prediction_id: string) =>
    request<EvolutionPrediction>('GET', `/api/evolution/predictions/${prediction_id}`),
  evolutionHistoryEvents: () =>
    request<EvolutionHistoryEvent[]>('GET', '/api/evolution/history/events'),
  evolutionLearningDataset: () =>
    request<EvolutionLearningDatasetRow[]>('GET', '/api/evolution/history/learning-dataset'),
  evolutionValidationEvidence: () =>
    request<ValidationEvidence[]>('GET', '/api/evolution/validation/evidence'),
  evolutionValidationClaim: (claim_id: string) =>
    request<ValidationEvidence>('GET', `/api/evolution/validation/evidence/${claim_id}`),

  // E1 Innovation
  innovationAdjacency: (category: string) => request('POST', '/api/innovation/adjacency/score', { category }),
  innovationVisualIp: (brief: string) => request('POST', '/api/innovation/visual-ip/assist', { brief }),
  innovationRecipeToCost: (formula: Record<string, unknown>) => request('POST', '/api/innovation/recipe-to-cost', { formula }),
  // E2 Sourcing
  sourcingProfitAtRiskLatest: () => request('GET', '/api/sourcing/profit-at-risk/latest'),
  sourcingConsolidate: (category: string) => request('POST', '/api/sourcing/supplier/consolidate', { category }),
  sourcingResilience: (scenario: string) => request('POST', '/api/sourcing/resilience/stress-test', { scenario }),
  // E3 Manufacturing
  manufacturingRecallCheck: (batch_id: string) => request('POST', '/api/manufacturing/recall/check', { batch_id }),
  // E4 Distribution
  distributionRouteOptimize: (window: string) => request('POST', '/api/distribution/route/optimize', { window }),
  // E5 Go-to-Market
  gtmConceptFromSignal: (social_signal: string) => request('POST', '/api/gtm/fandom/concept-from-signal', { social_signal }),
  gtmServiceAutomate: (ticket: string) => request('POST', '/api/gtm/service/automate', { ticket }),
  gtmServiceGuarantee: (complaint: string) => request('POST', '/api/gtm/service/guarantee', { complaint }),
  // E7 Corp Dev
  mnaFreightRedundancy: (target_id: string) => request('POST', '/api/mna/freight-redundancy', { target_id }),
  mnaPartnership: (partner: string) => request('POST', '/api/mna/partnership/structure', { partner }),

  // V7 §12 — interpretation layer
  alerts: () => request<import('../types').AlertItem[]>('GET', '/api/alerts'),
  alertDetail: (id: string) => request<import('../types').AlertDetail>('GET', `/api/alerts/${encodeURIComponent(id)}`),
  interpretationHeadline: (view: string, state: Record<string, unknown>) =>
    request<import('../types').InterpretationHeadline>('POST', '/api/interpretation/headline', { view, state }),

  // Consulting layer (spec 11) — these endpoints accept the demo passcode like the rest.
  ask: (question: string) => request<import('../types').AskResponse>('POST', '/api/ask', { question }),
  context: (engineId: number, interventionId?: string) =>
    request<import('../types').ContextRecord>('GET', interventionId ? `/api/context/${engineId}/${interventionId}` : `/api/context/${engineId}`),
  explain: (body: import('../types').ExplainRequest) =>
    request<import('../types').ExplainResponse>('POST', '/api/explain', body),
  ingest: (body: { source_text?: string; source_url?: string; source_ref?: string }) =>
    request<import('../types').IngestResponse>('POST', '/api/ingest', body),
  ingestUpload: (file: File, source_ref?: string) => {
    const form = new FormData();
    form.append('file', file);
    if (source_ref) form.append('source_ref', source_ref);
    return requestForm<import('../types').IngestResponse>('/api/ingest/upload', form);
  },
  ingestQueue: () => request<import('../types').IngestCandidate[]>('GET', '/api/ingest/queue'),
  ingestConfirm: (id: string, value?: number | null) =>
    request<import('../types').IngestConfirmResponse>('POST', `/api/ingest/${id}/confirm`, { value: value ?? null }),
  ingestReject: (id: string, reason?: string) =>
    request<import('../types').IngestCandidate>('POST', `/api/ingest/${id}/reject`, { reason: reason ?? null }),
  learningInterventions: () => request<import('../types').LearningGridResponse>('GET', '/api/learning/interventions'),
  synthesisAgenda: () => request<import('../types').SynthesisResponse>('GET', '/api/synthesis/agenda'),
  emergingSignals: () => request<import('../types').EmergingResponse>('GET', '/api/signals/emerging'),
  assumptionPressure: () => request<import('../types').AssumptionPressureResponse>('GET', '/api/assumptions/pressure'),
  briefingLatest: (kind?: 'daily' | 'weekly') =>
    request<import('../types').BriefingResponse>('GET', `/api/briefing/latest${kind ? `?kind=${kind}` : ''}`),
  memoryDecisions: (filters: Record<string, string | undefined> = {}) => {
    const qs = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== '') qs.set(key, value);
    }
    const query = qs.toString();
    return request<import('../types').MemoryDecisionsResponse>('GET', `/api/memory/decisions${query ? `?${query}` : ''}`);
  },
  memoryDecision: (id: string) =>
    request<import('../types').MemoryDecisionDetail>('GET', `/api/memory/decision/${encodeURIComponent(id)}`),
  opportunities: () => request<import('../types').OpportunityLedgerResponse>('GET', '/api/opportunities'),
  opportunityDisposition: (id: string, action: import('../types').OpportunityAction, reason?: string, decider?: string) =>
    request<import('../types').OpportunityDispositionResponse>(
      'POST',
      `/api/opportunities/${encodeURIComponent(id)}/disposition`,
      { action, reason, decider },
    ),
  connectors: () => request<import('../types').ConnectorsResponse>('GET', '/api/connectors'),
};
